import {ErrorMessage, Loader, SharePreviewCard} from '@communityshare/ui-shared';
import {useConnect, useQueryMutation} from '@lore/query-connect';
import {PayloadStates} from '@lore/utils';
import {useConfig} from '@lore/config';
import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import useEventImage from '@/features/share-event/hooks/useEventImage';
import usePreviewImages from '@/features/share-common/hooks/usePreviewImages';
import getEventShareUrl from '@/features/share-event/utils/getEventShareUrl';

// Window name for the preview tab: repeat clicks reuse the tab instead of
// opening new ones.
const PREVIEW_WINDOW_NAME = 'cs-share-event-preview';

EventLinkPreview.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.number.isRequired,
    state: PropTypes.string,
    data: PropTypes.shape({
      activityId: PropTypes.number,
      siteId: PropTypes.number,
      title: PropTypes.string,
      shared: PropTypes.bool
    })
  }).isRequired
};

export default function EventLinkPreview(props) {
  const { event } = props;
  const { t } = useTranslation();
  const config = useConfig();
  // Default to tag 1 so byId never throws for events without an activity
  const activity = useConnect('tag.byId', {
    id: event?.data?.activityId ?? 1
  });
  const siteId = event?.data?.siteId ?? null;
  // Pass 0 when siteId is absent, so the blueprint doesn't throw 'Missing id';
  // a 0-id query returns NOT_FOUND harmlessly. Location is omitted in the
  // render when siteId is null (see below).
  const site = useConnect('site.byId', {
    id: siteId ?? 0
  });
  // Local selection; null defers to the saved image (or the default)
  const [selectedImageId, setSelectedImageId] = useState(null);
  const eventImage = useEventImage(event.id);
  const { loading: imagesLoading, images } = usePreviewImages(event.data.activityId);
  const defaultSavedRef = useRef(false);
  const markSharedRef = useRef(false);
  const sharedMutation = useQueryMutation('event.update', event);

  const shareUrl = getEventShareUrl(config, event);
  const shareHostname = new URL(shareUrl).hostname;

  // Opening the share dialog counts as sharing the event, so flip the flag
  // once if it isn't already set. A failed write is logged and retried on a
  // later render; it never blocks the UI.
  useEffect(() => {
    const isReady = [PayloadStates.RESOLVED, PayloadStates.UPDATING].includes(event.state) && event.data;
    if (isReady && !event.data.shared && !markSharedRef.current) {
      markSharedRef.current = true;
      sharedMutation.mutateAsync({ shared: true }).catch((error) => {
        markSharedRef.current = false;
        console.error('Failed to mark event as shared', error);
      });
    }
  }, [event.state, event.data]);

  // An event with no saved image gets the default persisted once,
  // so the share page renders the same photo the preview shows.
  // Waits for the activity's image set so the right default is saved.
  useEffect(() => {
    if (eventImage.isSuccess && !eventImage.image && !imagesLoading && !defaultSavedRef.current) {
      defaultSavedRef.current = true;
      const image = images[0];
      eventImage.upsert({ imageUrl: image.src, width: image.width, height: image.height });
    }
  }, [eventImage.isSuccess, eventImage.image, imagesLoading, images]);

  // loading event, its saved share image, or the activity's image set
  if ([PayloadStates.FETCHING, PayloadStates.INITIAL_STATE].includes(event.state) || eventImage.isLoading || imagesLoading) {
    return (
      <div className="flex flex-col justify-center h-[80vh]">
        <Loader/>
      </div>
    );
  }

  // unexpected error state
  if (![PayloadStates.RESOLVED, PayloadStates.UPDATING].includes(event.state) || !event.data) {
    return (
      <div className="flex flex-col items-center">
        <ErrorMessage v2={true}>
          {t('event_share.data_error')}
        </ErrorMessage>
      </div>
    );
  }

  // Local selection wins (optimistic), then the saved image;
  // null renders the placeholder until an image exists
  const savedImage = images.find(image => image.src === eventImage.image?.imageUrl);
  const shareImage = images.find(image => image.id === selectedImageId) ?? savedImage ?? null;

  // The preview swaps immediately while the save runs;
  // a failure surfaces through eventImage.upsertError below.
  function onShareImageChange(imageId) {
    setSelectedImageId(imageId);
    const image = images.find(image => image.id === imageId);
    eventImage.upsert({
      imageUrl: image.src,
      width: image.width,
      height: image.height
    });
  }

  function onPreview() {
    window.open(shareUrl, `${PREVIEW_WINDOW_NAME}-${event.id}`);
  }

  const location = siteId && site.state !== PayloadStates.FETCHING ?
    [site?.data?.city, site?.data?.state].filter(Boolean).join(', ') :
    '';
  const activityName = activity?.data?.name ?? '...';

  return (
    <SharePreviewCard
      title={t('event_share.link_preview')}
      imageSrc={shareImage?.src ?? null}
      imageKey={shareImage?.id}
      imageAlt={t('event_share.preview_image_alt')}
      urlText={`${shareHostname}/share/event/${event.id}`}
      eyebrowText={
        <>
          {activity.state === PayloadStates.FETCHING && <Loader/>}
          {location ? `${activityName} | ${location}` : activityName}
        </>
      }
      primaryText={t('event_share.cta_line')}
      secondaryText={<>&ldquo;{event.data.title}&rdquo;</>}
      previewLabel={t('event_share.preview_page')}
      onPreview={onPreview}
      images={images}
      selectedImageId={shareImage?.id ?? null}
      onImageChange={onShareImageChange}
      swapPhotoLabel={t('event_share.swap_photo')}
      getImageLabel={(image, index) => t('event_share.photo_option', { number: index + 1 })}
      errorMessage={eventImage.upsertError ? t('event_share.image_error') : null}
    />
  );
}
